import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useMemo } from 'react'

import type { LoginResponse, MeResponse } from '@/lib/api'
import type { ServerFlagKey } from '@/lib/feature-flags'
import { ApiError, authApi } from '@/lib/api'
import { identifyUser, posthog, resetPostHog } from '@/lib/posthog'
import { arePermissionsReady } from '@/lib/permissions-ready'
import { unsubscribeFromPush } from '@/lib/push-notifications'
import { announceSessionStarted } from '@/lib/auth-session-signal'

interface User {
    id: string
    username: string
}

export interface ImpersonationState {
    actor: { id: string; username: string }
    target: { id: string; username: string }
}

interface AuthState {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
}

interface LoginResult {
    success: boolean
    // Tài khoản bật OTP: mật khẩu đúng nhưng chưa có phiên. Gọi verifyLoginOtp
    // với challengeToken + mã 6 số mới thực sự đăng nhập.
    otpRequired?: boolean
    challengeToken?: string
    emailMasked?: string
    resendAfterSeconds?: number
}

interface AuthContextValue extends AuthState {
    login: (data: {
        username: string
        password: string
    }) => Promise<LoginResult>
    verifyLoginOtp: (challengeToken: string, code: string) => Promise<void>
    logout: () => Promise<void>
    checkAuth: () => Promise<void>
    permissions: string[]
    // Mã vai trò đang bật của người dùng hiệu lực. Quản trị phân quyền gác theo
    // vai trò `admin` chứ không theo permission — ai giữ `permissions.assign`
    // có thể tự cấp cho mình mọi quyền, nên permission không đủ để gác.
    roles: string[]
    isAdmin: boolean
    // True once the /me query has settled, so consumers can tell an
    // intentionally empty permission set apart from "not loaded yet". login()
    // seeds permissions:[] synchronously then background-refetches, so a bare
    // permissions.length check would briefly look empty for a privileged user.
    arePermissionsLoaded: boolean
    hasPermission: (permission: string) => boolean
    hasAnyPermission: (...permissions: string[]) => boolean
    hasAllPermissions: (...permissions: string[]) => boolean
    // Non-null only while an admin is viewing as another user. `user`/`permissions`
    // above are the EFFECTIVE (target) identity; this exposes the actor too.
    impersonating: ImpersonationState | null
    isImpersonating: boolean
    /** SHOP_ENABLED của bản deploy (/auth/me); không phải true = ẩn mọi màn bán lẻ. */
    shopEnabled?: boolean
    /** ZALO_OA_ENABLED của bản deploy (/auth/me); không phải true = ẩn Zalo ZBS. */
    zaloOAEnabled?: boolean
    /** Cờ server theo khoá (`server:shop`, `server:zalo-oa`); chưa biết = false. */
    serverFlagOn: (flag: ServerFlagKey) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)
export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const
const EMPTY_PERMISSIONS: string[] = []

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export interface MeQueryData {
    user: User | null
    permissions: string[]
    roles: string[]
    impersonating: ImpersonationState | null
    /** undefined = chưa đọc /auth/me (vd ngay sau đăng nhập, trước lượt tải lại). */
    shopEnabled?: boolean
    /** Như shopEnabled, cho ZALO_OA_ENABLED. */
    zaloOAEnabled?: boolean
}

export const ADMIN_ROLE_CODE = 'admin'

// parseMeResponse maps the /auth/me payload to the cached auth state. The
// primary user is the EFFECTIVE user (the impersonated target while viewing as);
// the impersonating block carries both identities. Shared by the meQuery and the
// _authed beforeLoad so the two parses cannot drift.
export function parseMeResponse(data: MeResponse | undefined): MeQueryData {
    if (!data)
        return { user: null, permissions: [], roles: [], impersonating: null }
    return {
        user: { id: data.user_id, username: data.username },
        permissions: (data.permissions as string[]) ?? [],
        roles: (data.roles as string[]) ?? [],
        impersonating: data.impersonating ?? null,
        // Server cũ chưa trả trường → để trống (route bán lẻ coi như tắt).
        ...(data.shop_enabled === undefined
            ? {}
            : { shopEnabled: data.shop_enabled }),
        ...(data.zalo_oa_enabled === undefined
            ? {}
            : { zaloOAEnabled: data.zalo_oa_enabled }),
    }
}

// meQueryFn fetches and parses /auth/me, returning the empty (logged-out) state
// on any error. Shared by the provider's meQuery and the _authed beforeLoad so
// the two cannot diverge.
export async function meQueryFn(): Promise<MeQueryData> {
    try {
        const response = await authApi.me()
        return parseMeResponse(response.data)
    } catch (error) {
        // Only a genuine 401 means logged out → empty state. Transient/network
        // errors must NOT collapse to empty: that would flip isImpersonating off
        // and fire a spurious hard reset. Re-throw so React Query keeps the last
        // good data and surfaces isError instead.
        if (error instanceof ApiError && error.status === 401) {
            return {
                user: null,
                permissions: [],
                roles: [],
                impersonating: null,
            }
        }
        throw error
    }
}

export function useAuthProvider(): AuthContextValue {
    const queryClient = useQueryClient()

    const meQuery = useQuery({
        queryKey: AUTH_ME_QUERY_KEY,
        retry: false,
        queryFn: meQueryFn,
    })

    const loginMutation = useMutation({
        mutationFn: async (data: { username: string; password: string }) => {
            const response = await authApi.login(data)
            if (!response.data) {
                throw new Error('Login failed')
            }
            return response.data
        },
    })

    const logoutMutation = useMutation({
        mutationFn: async () => {
            await authApi.logout()
        },
        onSettled: () => {
            posthog.capture('user_logged_out')
            queryClient.clear()
            queryClient.setQueryData(AUTH_ME_QUERY_KEY, null)
            resetPostHog()
        },
    })

    // seedSession nạp danh tính vào cache ngay sau khi server cấp phiên, tránh
    // vòng lặp redirect giữa _app (state React) và /login (đọc cache). Dùng chung
    // cho đăng nhập thường và đăng nhập qua OTP để hai đường không lệch nhau.
    const seedSession = (loginData: LoginResponse) => {
        queryClient.setQueryData<MeQueryData>(AUTH_ME_QUERY_KEY, {
            user: { id: loginData.user.id, username: loginData.user.username },
            permissions: [],
            roles: [],
            impersonating: null,
        })
        // Background refresh to load full permissions
        void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
        // Live streams in other tabs stalled on the dead session reconnect now.
        announceSessionStarted()
    }

    const login = async (data: {
        username: string
        password: string
    }): Promise<LoginResult> => {
        const loginData = await loginMutation.mutateAsync(data)
        if (loginData.otp_required) {
            // Chưa có phiên nào: KHÔNG nạp cache, nếu không app sẽ tưởng đã đăng
            // nhập và cho vào trong khi server chưa cấp token.
            return {
                success: false,
                otpRequired: true,
                challengeToken: loginData.challenge_token,
                emailMasked: loginData.email_masked,
                resendAfterSeconds: loginData.resend_after_seconds,
            }
        }
        seedSession(loginData)
        return { success: true }
    }

    const verifyLoginOtp = async (challengeToken: string, code: string) => {
        const response = await authApi.verifyOtp({
            challenge_token: challengeToken,
            code,
        })
        if (!response.data) {
            throw new Error('OTP verification failed')
        }
        seedSession(response.data)
    }

    const logout = async () => {
        // Huỷ push TRƯỚC khi gọi API đăng xuất — `/push/unsubscribe` cần token
        // còn sống. Subscription thuộc về trình duyệt chứ không thuộc tài khoản:
        // để nguyên thì người đăng nhập sau trên cùng máy thấy chuông "đã bật"
        // trong khi server còn ghi endpoint đó thuộc chủ cũ, và máy này vẫn
        // nhận thông báo của người đã đăng xuất.
        try {
            await unsubscribeFromPush()
        } catch {
            // Không chặn đăng xuất vì dọn push thất bại. `ensureSubscription`
            // của lần đăng nhập sau sẽ giành lại quyền sở hữu endpoint.
        }
        try {
            await logoutMutation.mutateAsync()
        } catch {
            // ignore logout errors
        }
    }

    const checkAuth = async () => {
        await queryClient.refetchQueries({ queryKey: AUTH_ME_QUERY_KEY })
    }

    const user = meQuery.data?.user ?? null
    const permissions = meQuery.data?.permissions ?? EMPTY_PERMISSIONS
    const roles = meQuery.data?.roles ?? EMPTY_PERMISSIONS
    const isAdmin = roles.includes(ADMIN_ROLE_CODE)
    const impersonating = meQuery.data?.impersonating ?? null
    const shopEnabled = meQuery.data?.shopEnabled === true
    const zaloOAEnabled = meQuery.data?.zaloOAEnabled === true
    const isImpersonating = impersonating !== null
    const isAuthenticated = Boolean(user)
    const isLoading = meQuery.isLoading
    // Ready once we have real permissions, OR the fetch has settled with none.
    // Treating "have data, refreshing" as ready keeps a background refetch
    // (e.g. network reconnect) from unmounting the dashboard, while the
    // post-login empty+refetching window still reads as not-ready (no flash).
    const arePermissionsLoaded = arePermissionsReady({
        permissionCount: permissions.length,
        isFetched: meQuery.isFetched,
        isFetching: meQuery.isFetching,
        isError: meQuery.isError,
    })

    // Analytics identity stays the ACTOR (admin) while impersonating, so the
    // target's identity never pollutes PostHog/Sentry.
    useEffect(() => {
        if (impersonating) {
            identifyUser(impersonating.actor, true)
        } else if (user) {
            identifyUser(user)
        }
    }, [user, impersonating])

    // useMemo required here — this is a context provider value.
    // React Compiler cannot stabilize context values automatically.
    return useMemo(() => {
        const permissionSet = new Set(permissions)
        return {
            user,
            isAuthenticated,
            isLoading,
            login,
            verifyLoginOtp,
            logout,
            checkAuth,
            permissions,
            roles,
            isAdmin,
            arePermissionsLoaded,
            hasPermission: (permission: string) =>
                permissionSet.has(permission),
            hasAnyPermission: (...perms: string[]) =>
                perms.some((p) => permissionSet.has(p)),
            hasAllPermissions: (...perms: string[]) =>
                perms.every((p) => permissionSet.has(p)),
            impersonating,
            isImpersonating,
            shopEnabled,
            zaloOAEnabled,
            serverFlagOn: (flag: ServerFlagKey) =>
                ({
                    'server:shop': shopEnabled,
                    'server:zalo-oa': zaloOAEnabled,
                })[flag],
        }
    }, [
        user,
        isAuthenticated,
        isLoading,
        permissions,
        roles,
        isAdmin,
        arePermissionsLoaded,
        impersonating,
        isImpersonating,
        shopEnabled,
        zaloOAEnabled,
    ])
}

export { AuthContext }
